/**
 * useCreateDropRealmSignin — codeless Realm sign-in at a Drop's door (T15).
 *
 * The host-neutral sibling of useCreateChallengeRealmSignin: calls
 * POST /drops/:slug/realm-signin, which runs the same signed-handshake flow
 * with the Drop's onboarding-experience config. The X-Realm-UUID/TS/Sig
 * values come from rttrStore (set by the embed bootstrap) and are passed as
 * the generated method's named header arguments.
 *
 * A 4xx (no email on file, non-REALM experience, no experience) is a normal
 * fall-through to the standard email + code login — the caller catches it.
 */
import { useMutation } from "@tanstack/react-query";
import { DropsApi, Token } from "@devour/client";
import { useSelector } from "react-redux";
import getConfig from "@/utils/getConfig";
import { IStore } from "@/redux/defaultStore";

interface CreateDropRealmSigninVars {
    slug: string;
}

export function useCreateDropRealmSignin() {
    const realmUuid = useSelector((store: IStore) => store.rttrStore?.realmUuid);
    const realmTs = useSelector((store: IStore) => store.rttrStore?.realmTs);
    const realmSig = useSelector((store: IStore) => store.rttrStore?.realmSig);

    return useMutation({
        mutationFn: async ({ slug }: CreateDropRealmSigninVars): Promise<Token> => {
            const api = new DropsApi(getConfig());
            return api.createDropsSlugRealmSignin({
                slug,
                xRealmUUID: realmUuid!,
                xRealmTS: realmTs!,
                xRealmSig: realmSig!,
            });
        },
    });
}
